import type { SelectedSystemType } from 'data/types';
import type { QuotableSystemAccessoryRecommendationType } from 'features/api/accessories-api-types';
import {
  isDuctedBundleSystem,
  readCompatibilityPairs
} from 'utils/compatibility-bundle-fields';
import type { DuctedLineSetRequiredLengths } from 'utils/ducted-line-set-length';

export const PAIR_RECOMMENDATION_PRODUCT_TYPE = 'pair';
const CONDENSER_RECOMMENDATION_PRODUCT_TYPE = 'condenser';
const SYSTEM_RECOMMENDATION_PRODUCT_TYPE = 'system';

export function attributePairRecommendations(
  system: SelectedSystemType,
  recommendations: QuotableSystemAccessoryRecommendationType[]
): QuotableSystemAccessoryRecommendationType[] {
  const condenserByConfiguration = new Map(
    readCompatibilityPairs(system).map((pair) => [
      pair.configurationUniqueId,
      pair.condenserUniqueId
    ])
  );
  return recommendations.map((rec) => {
    if (rec.productType !== PAIR_RECOMMENDATION_PRODUCT_TYPE) return rec;
    const condenserUniqueId = condenserByConfiguration.get(rec.uniqueId);
    return condenserUniqueId
      ? {
          ...rec,
          productType: CONDENSER_RECOMMENDATION_PRODUCT_TYPE,
          productId: 0,
          uniqueId: condenserUniqueId
        }
      : {
          ...rec,
          productType: SYSTEM_RECOMMENDATION_PRODUCT_TYPE,
          productId: 0,
          uniqueId: system.uniqueId
        };
  });
}

export function toRequiredLineSetLengthRequest(
  system: SelectedSystemType,
  lengths: DuctedLineSetRequiredLengths
): Record<string, number> | undefined {
  const entries: [string, number][] = isDuctedBundleSystem(system)
    ? readCompatibilityPairs(system).flatMap((pair) => {
        const length = lengths[pair.condenserUniqueId];
        return length === undefined
          ? []
          : [[pair.configurationUniqueId, length] as [string, number]];
      })
    : system.configuration.condensers.flatMap((condenser) => {
        const length = lengths[condenser.uniqueId];
        return length === undefined
          ? []
          : [[system.uniqueId, length] as [string, number]];
      });
  return entries.length === 0 ? undefined : Object.fromEntries(entries);
}
